import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button } from '@material-ui/core';
import clsx from 'clsx';
import styles from './AddCountryOrColourForm.module.css';
import { ValidatingTextField } from '../FormComponents';
import {
    API_RESOURCE_URLS,
    DISPLAY_MESSAGES,
    MESSAGE_TYPE,
    ADD_COLOUR_OR_COUNTRY,
    REGEX
} from '../../constants';
import { usePopupManager } from '../../providers/PopupManager/PopupManager';
import { useErrorHandler } from 'react-error-boundary';
import { buildErrorMessage } from '../../apis/calls';
import { API } from '../../apis/api';


const AddCountryOrColourForm = ({ open, handleClose, formType }) => {


    const { showPopup } = usePopupManager();
    const handleError = useErrorHandler();

    const [countriesArray, setCountriesArray] = useState({data:[]});
    const [coloursArray, setColoursArray] = useState({data:[]});
    const [countryName, setCountryName] = useState('');
    const [countryCode, setCountryCode] = useState('');
    const [colourName, setColourName] = useState('');
    const [colourCode, setColourCode] = useState('');
    const [highlightMandatoryFields, setHighlightMandatoryFields] = useState(false);
    const [displayCombinationNotUnique,setDisplayCombinationNotUnique] = useState(false);

    const clearStates = () => {
        setCountryName(null);
        setCountryCode(null);
        setColourName(null);
        setColourCode(null);
        setHighlightMandatoryFields(false);
        setDisplayCombinationNotUnique(false);
    }

    const loadData = async (setState, url, params) => {
        try {
            const response = await API.get(url, {
                params: params,
            });
            setState({ data: response.data });
        } catch (error) {
        console.error(buildErrorMessage(error));
        handleError(error);
        }
    };

    const loadCountries = async () => {
        return await loadData(setCountriesArray, API_RESOURCE_URLS.COUNTRIES);
    };

    const loadColours = async () => {
        return await loadData(setColoursArray, API_RESOURCE_URLS.COLOURS);
    };

    const checkIfCodeAlreadyExists = (newCode, existingCodesSet) => {
        return existingCodesSet.has(newCode);
    };
    const getExistingCountryCodes= (countriesArray) => {
        const countryCodesSet = new Set();
        countriesArray.forEach(countryData => { countryCodesSet.add(countryData.code); });
        return countryCodesSet;
    };
    const getExistingColourCodes= (coloursArray) => {
        const colourCodesSet = new Set();
        coloursArray.forEach(colourData => { colourCodesSet.add(colourData.colourCode); });
        return colourCodesSet;
    };

    const runPreSubmissionChecks = (formType) => {
        if (formType === ADD_COLOUR_OR_COUNTRY.COUNTRY) {
            const isCombinationUnique = checkIfCodeAlreadyExists(countryCode, getExistingCountryCodes(countriesArray.data));
            if (isCombinationUnique) {
                setDisplayCombinationNotUnique(true);
                return false;
            }
            if (countryCode === null || countryCode === '' || countryName === null || countryName === '') {
                setHighlightMandatoryFields(true);
                return false;
            }
            if (!isValidCountryName(countryName) || !isValidCountryCode(countryCode)) return false;
        }
        else if (formType === ADD_COLOUR_OR_COUNTRY.COLOUR) {
            const isCombinationUnique = checkIfCodeAlreadyExists(colourCode, getExistingColourCodes(coloursArray.data));
            if (isCombinationUnique) {
                setDisplayCombinationNotUnique(true);
                return false;
            }
            if (colourCode === null || colourCode === '' || colourName === null || colourName === '') {
                setHighlightMandatoryFields(true);
                return false;
            }
            if (!isValidColourName(colourName) || !isValidColourCode(colourCode)) return false;
        }
        return true;
    }

    const handleSubmit = async (formType) => {
        try {
            if (formType === ADD_COLOUR_OR_COUNTRY.COUNTRY) {
                const data = [{
                    code: countryCode,
                    country: countryName
                }];
                await API.post(API_RESOURCE_URLS.COUNTRIES, data,);
                showPopup({
                    type: MESSAGE_TYPE.SUCCESS,
                    contextText: DISPLAY_MESSAGES.COUNTRY_ADD_SUCCESS,
                    info: `Name: ${countryName}, Code: ${countryCode}`,
                });
            }
            else if (formType === ADD_COLOUR_OR_COUNTRY.COLOUR) {
                const data = [{
                    colourCode: colourCode,
                    description: colourName
                }];
                await API.post(API_RESOURCE_URLS.COLOURS, data,);
                showPopup({
                    type: MESSAGE_TYPE.SUCCESS,
                    contextText: DISPLAY_MESSAGES.COLOUR_ADD_SUCCESS,
                    info: `Name: ${colourName}, Code: ${colourCode}`,
                });
            }
            handleClose();
            return;
        }
        catch (error) {
            console.error(buildErrorMessage(error));
            if (formType === ADD_COLOUR_OR_COUNTRY.COUNTRY) {
                showPopup({
                    type: MESSAGE_TYPE.FAILURE,
                    contextText: DISPLAY_MESSAGES.COUNTRY_ADD_FAILURE,
                    info: 'Failed to add country.',
                    error: error,
                });
            }
            else {
                showPopup({
                    type: MESSAGE_TYPE.FAILURE,
                    contextText: DISPLAY_MESSAGES.COLOUR_ADD_FAILURE,
                    info: 'Failed to add colour.',
                    error: error,
                });
            }
            
        }
    };

    useEffect(() => {
        if (open) {
            loadCountries();
            loadColours();
        } else {
            clearStates();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);
    
    const isValidCountryName = valueString => {
        if (typeof valueString !== 'string' || valueString.trim() === '') return false;
        const regex = REGEX.COUNTRY_NAME;
        return regex.test(valueString);
    };
    const isValidCountryCode = valueString => {
        if (typeof valueString !== 'string' || valueString.trim() === '') return false;
        const regex = REGEX.COUNTRY_CODE;
        return regex.test(valueString);
    };
    const isValidColourCode = valueString => {
        if (typeof valueString !== 'string' || valueString.trim() === '') return false;
        const regex = REGEX.COLOUR_CODE;
        return regex.test(valueString);
    };
    const isValidColourName = valueString => {
        if (typeof valueString !== 'string' || valueString.trim() === '') return false;
        const regex = REGEX.COLOUR_NAME;
        return regex.test(valueString);
    };

    const renderFormType = (formType) => {
        if (formType === ADD_COLOUR_OR_COUNTRY.COUNTRY) {
            var nameLabel = "Country Name";
            var nameValue = countryName;
            var nameInputTestId = 'add-country-name-input';
            var nameOnChangeFn = (e) => { return setCountryName(e.toUpperCase()) };
            var nameValidationFn = () => { return isValidCountryName };
            var codeLabel = "Country Code";
            var codeValue = countryCode;
            var codeValidationFn = () => { return isValidCountryCode };
            var codeInputTestId = 'add-country-code-input';
            var codeOnChangeFn = (e) => { return setCountryCode(e.toUpperCase()) };
            var duplicateDisplayMessage = DISPLAY_MESSAGES.DUPLICATE_COUNTRY_CODE;

        }
        else {
            var nameLabel = "Colour Name";
            var nameValue = colourName;
            var nameInputTestId = 'add-colour-name-input';
            var nameOnChangeFn = (e) => { return setColourName(e.toUpperCase()) };
            var nameValidationFn = () => { return isValidColourName };
            var codeLabel = "Colour Code";
            var codeValue = colourCode;
            var codeValidationFn = () => { return isValidColourCode };
            var codeInputTestId = 'add-colour-code-input';
            var codeOnChangeFn = (e) => { return setColourCode(e.toUpperCase()) };
            var duplicateDisplayMessage = DISPLAY_MESSAGES.DUPLICATE_COLOUR_CODE;
        }
        return (
            <>
                <div className={clsx(styles.column, styles.newLable)}>
                    <label className={clsx(styles.label)}>{nameLabel}</label>
                    <ValidatingTextField
                        isMandatory
                        markIfEmpty={highlightMandatoryFields}
                        validationFn={nameValidationFn()}
                        validationHelperText={'Invalid name'}
                        isQuantityValid
                        variant="outlined"
                        value={nameValue}
                        onChange={e => {
                            nameOnChangeFn(e.target.value)
                        }}
                        placeholder="(Enter Name)"
                        className={styles.newText}
                        inputProps={{
                            className: styles.newTextInput,
                            'data-testid': nameInputTestId,
                        }}
                        FormHelperTextProps={{
                            className: styles.helperText,
                        }}
                    />
                </div>
                <div className={clsx(styles.column, styles.newLable)}>
                    <label className={clsx(styles.label)}>
                        {codeLabel}
                    </label>
                    <ValidatingTextField
                        isMandatory
                        markIfEmpty={highlightMandatoryFields}
                        validationFn={codeValidationFn()}
                        validationHelperText={'Invalid code'}
                        isQuantityValid
                        variant="outlined"
                        value={codeValue}
                        onChange={e => {
                            codeOnChangeFn(e.target.value)
                            setDisplayCombinationNotUnique(false);
                        }}
                        showError={displayCombinationNotUnique}
                        placeholder="(Enter Code)"
                        className={styles.newText}
                        inputProps={{
                            className: styles.newTextInput,
                            'data-testid': codeInputTestId,
                        }}
                        FormHelperTextProps={{
                            className: styles.helperText,
                        }}
                    />
                    <span className={clsx(styles.errorText)}>
                        {displayCombinationNotUnique &&
                            duplicateDisplayMessage}
                    </span>
                </div>
            </>
        );
    };

    return (
        <Modal open={open} onClose={handleClose} disableBackdropClick>
            <div className={styles.addCountryOrColour}>
                <div className={styles.addCountryOrColourTitle}>
                    <span>{formType}</span>
                    <span className={styles.rowTitleTop}>Basic details</span>
                </div>
                <div className={clsx(styles.formSec, styles.vcDetail)}>
                    <div >
                        <div className={styles.formRow}>
                            {renderFormType(formType)}
                        </div>
                    </div>
                </div>
                <div className={styles.buttons}>
                    <button
                        className={styles.submit}
                        onClick={e => {
                                if (e.detail > 1) {
                                    return;
                                }
                                return runPreSubmissionChecks(formType) && handleSubmit(formType);
                            }
                        }
                    >
                        Submit
                    </button>
                    <Button onClick={handleClose} className={styles.cancel}>Cancel</Button>
                </div>
            </div>
        </Modal>
    );
};

AddCountryOrColourForm.propTypes = {
    open: PropTypes.bool.isRequired,
    handleClose: PropTypes.func.isRequired,
    formType: PropTypes.string.isRequired,
};

export default AddCountryOrColourForm;
